import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date) {
  return new Date(date).toLocaleDateString('de-DE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function formatFileSize(bytes: number) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Currency configuration for supported currencies
export const CURRENCY_CONFIG: Record<
  string,
  {
    symbol: string;
    locale: string;
    position: 'before' | 'after';
  }
> = {
  EUR: { symbol: '€', locale: 'de-DE', position: 'after' },
  USD: { symbol: '$', locale: 'en-US', position: 'before' },
  GBP: { symbol: '£', locale: 'en-GB', position: 'before' },
  CHF: { symbol: 'CHF', locale: 'de-CH', position: 'after' },
};

/**
 * Format a number as currency with the appropriate symbol and locale.
 * @param value - The numeric value to format
 * @param currency - ISO 4217 currency code (EUR, USD, GBP, CHF)
 * @returns Formatted currency string (e.g., "1.234 €" or "$1,234")
 */
export function formatCurrency(value: number, currency: string = 'EUR'): string {
  const config = CURRENCY_CONFIG[currency] || CURRENCY_CONFIG.EUR;
  const formatted = new Intl.NumberFormat(config.locale, {
    maximumFractionDigits: 0,
  }).format(value);

  return config.position === 'before' ? `${config.symbol}${formatted}` : `${formatted} ${config.symbol}`;
}

/**
 * Get the symbol for a given currency code.
 * @param currency - ISO 4217 currency code
 * @returns Currency symbol (e.g., "€", "$", "£", "CHF")
 */
export function getCurrencySymbol(currency: string): string {
  return CURRENCY_CONFIG[currency]?.symbol || '€';
}

/**
 * Normalize API base URL to avoid double path segments.
 *
 * Some deployments mistakenly set REACT_APP_API_BASE_URL to include "/api/backend".
 * Since endpoints already prefix with "/api/backend", this removes that suffix to
 * avoid calling ".../api/backend/api/backend/...".
 *
 * @param url - The raw API base URL from environment variables
 * @returns Normalized API base URL with trailing slashes and "/api/backend" suffix removed
 *
 * @example
 * normalizeApiBaseUrl('https://api.example.com/api/backend') // 'https://api.example.com'
 * normalizeApiBaseUrl('https://api.example.com/') // 'https://api.example.com'
 * normalizeApiBaseUrl('') // ''
 */
export function normalizeApiBaseUrl(url: string): string {
  if (!url) return '';

  return url
    .trim()
    .replace(/\/+$/, '')
    .replace(/\/api\/backend$/, '');
}
